import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../api/client';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import FilterTabs from '../../components/ui/FilterTabs';
import { useToast } from '../../context/ToastContext';

/**
 * Staff → Bot tasks / Launcher tasks (admin és owner) — a stáb feladat-táblája, 2026-10-11. A tulajdonos: „a webre egy
 * admin felületet, ahol fel tudok venni terveket és kipipálni, mi van meg, mit kell csinálni, mi a hiba, mit kell
 * tesztelni” + „a botnak és a launchernek kell két külön fül a weben”. A jóváhagyott látványterv:
 * https://claude.ai/artifact/8iNhjCJwBVZWpsyrmbiZ9T („tetszik”).
 *
 * Állapot szerinti csoportok — amire figyelni kell, az van felül (folyamatban, teendő, megírva-de-nincs-buildelve,
 * tesztelendő, ötlet; a kész alul, csukva). Egy sor kattintásra kinyílik: leírás, forrás, dátum, build, a kipipálható
 * tesztlista, áthelyezés, szerkesztés, törlés. A szerver: bot-backend controllers/staffTasks.controller.js.
 */

const STATUSES = [
  { id: 'idea',    label: 'Idea' },
  { id: 'todo',    label: 'To do' },
  { id: 'doing',   label: 'In progress' },
  { id: 'written', label: 'Written · not built', hint: 'In the source, not in a build yet' },
  { id: 'testing', label: 'Testing',             hint: 'In a build, not confirmed live yet' },
  { id: 'done',    label: 'Done' },
];
const GROUP_ORDER = ['doing', 'todo', 'written', 'testing', 'idea', 'done'];
const statusLabel = (id) => STATUSES.find(s => s.id === id)?.label || id;

// Az állapot pöttye: a tömör szín + egy halvány gyűrű ugyanabból (a látványterv `.tk-st`-je).
const DOT = {
  idea:    'bg-gray-600 shadow-[0_0_0_3px_rgba(95,95,105,0.28)]',
  todo:    'bg-gray-400 shadow-[0_0_0_3px_rgba(169,169,179,0.18)]',
  doing:   'bg-brand-500 shadow-[0_0_0_3px_rgba(164,139,255,0.22)]',
  written: 'bg-warn shadow-[0_0_0_3px_rgba(229,180,84,0.20)]',
  testing: 'bg-info shadow-[0_0_0_3px_rgba(122,167,245,0.20)]',
  done:    'bg-ok shadow-[0_0_0_3px_rgba(62,207,142,0.20)]',
};

const KINDS = {
  bug:     { label: 'Bug',     cls: 'badge-banned' },
  plan:    { label: 'Plan',    cls: 'badge' },
  feature: { label: 'Feature', cls: 'badge-brand' },
  test:    { label: 'Test',    cls: 'badge-frozen' },
};
const TYPE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'bug', label: 'Bugs' },
  { id: 'plan', label: 'Plans' },
  { id: 'feature', label: 'Features' },
  { id: 'test', label: 'Tests' },
];

// Ugyanazok a korlátok, mint a szerveren (staffTasks.controller.js LIMITS).
const LIMITS = { title: 200, details: 3000, source: 200, build: 100, test: 200, tests: 25 };

const SUBTITLE = {
  bot: 'Plans, bugs and tests for the bot (mortis2). Only staff can see this tab.',
  launcher: 'Plans, bugs and tests for the Mortis Launcher. Only staff can see this tab.',
};

// Az időpontok UTC-ben jönnek; a nap a stáb helyi idejében.
function day(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const errorText = (err, fallback) => err?.response?.data?.error || fallback;

function Icon({ d, className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"
         strokeLinejoin="round" aria-hidden="true" className={className}>
      {d}
    </svg>
  );
}
const CHEVRON = <path d="M6 9l6 6 6-6" />;
const SEARCH = <><circle cx="11" cy="11" r="7" /><path d="M20.5 20.5l-4.5-4.5" /></>;
const PLUS = <path d="M12 5v14M5 12h14" />;

function Dot({ status }) {
  return <span className={`w-[9px] h-[9px] flex-none rounded-full ${DOT[status] || DOT.idea}`} />;
}

/** Új tétel és szerkesztés. A tesztlista soronként; szerkesztéskor a változatlan sor megtartja a pipáját. */
function TaskForm({ initial, submitLabel, busy, onSubmit, onCancel }) {
  const [f, setF] = useState(() => ({
    title: initial?.title || '',
    kind: initial?.kind || 'bug',
    status: initial?.status || 'todo',
    source: initial?.source || '',
    build: initial?.build || '',
    details: initial?.details || '',
    tests: (initial?.tests || []).map(t => t.text).join('\n'),
  }));
  const set = (k) => (e) => setF(prev => ({ ...prev, [k]: e.target.value }));
  const idp = initial ? `tf${initial.id}` : 'tfnew';

  const submit = (e) => {
    e.preventDefault();
    if (!f.title.trim()) return;
    const wasDone = new Map((initial?.tests || []).map(t => [t.text, t.done]));
    const tests = f.tests.split('\n').map(s => s.trim()).filter(Boolean).slice(0, LIMITS.tests)
      .map(text => ({ text: text.slice(0, LIMITS.test), done: !!wasDone.get(text) }));
    onSubmit({
      title: f.title.trim(), kind: f.kind, status: f.status, source: f.source.trim(), build: f.build.trim(),
      details: f.details.trim(), tests,
    });
  };

  return (
    <form onSubmit={submit} className="grid gap-x-4 gap-y-3.5 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <label className="label" htmlFor={`${idp}-title`}>Title</label>
        <input id={`${idp}-title`} className="input" value={f.title} onChange={set('title')} required autoFocus
               maxLength={LIMITS.title} placeholder="What is it, in one line" />
      </div>
      <div>
        <label className="label" htmlFor={`${idp}-kind`}>Type</label>
        <select id={`${idp}-kind`} className="select" value={f.kind} onChange={set('kind')}>
          {Object.entries(KINDS).map(([id, k]) => <option key={id} value={id}>{k.label}</option>)}
        </select>
      </div>
      <div>
        <label className="label" htmlFor={`${idp}-status`}>Status</label>
        <select id={`${idp}-status`} className="select" value={f.status} onChange={set('status')}>
          {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>
      <div>
        <label className="label" htmlFor={`${idp}-source`}>Source</label>
        <input id={`${idp}-source`} className="input" value={f.source} onChange={set('source')}
               maxLength={LIMITS.source} placeholder="For example: user report, no log" />
      </div>
      <div>
        <label className="label" htmlFor={`${idp}-build`}>Build</label>
        <input id={`${idp}-build`} className="input" value={f.build} onChange={set('build')}
               maxLength={LIMITS.build} placeholder="For example: v2.4.7, or Not in a build yet" />
      </div>
      <div className="sm:col-span-2">
        <label className="label" htmlFor={`${idp}-details`}>Details</label>
        <textarea id={`${idp}-details`} className="textarea" value={f.details} onChange={set('details')}
                  maxLength={LIMITS.details} placeholder="What happens, what should happen, what changes" />
      </div>
      <div className="sm:col-span-2">
        <label className="label" htmlFor={`${idp}-tests`}>What to test</label>
        <textarea id={`${idp}-tests`} className="textarea min-h-[5.5rem]" value={f.tests} onChange={set('tests')}
                  placeholder="One check per line" />
        <p className="mt-1.5 text-xs text-gray-500">
          Every line becomes a box you can tick (at most {LIMITS.tests}, {LIMITS.test} characters each).
        </p>
      </div>
      <div className="sm:col-span-2 flex flex-wrap justify-end gap-2">
        <button type="button" className="btn-glass btn-sm" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="submit" className="btn-primary btn-sm" disabled={busy || !f.title.trim()}>{submitLabel}</button>
      </div>
    </form>
  );
}

function TaskRow({ task, open, editing, busy, delArmed, onToggle, onTick, onMove, onEdit, onSave, onCancelEdit, onDelete }) {
  const kind = KINDS[task.kind] || KINDS.plan;
  const ticked = task.tests.filter(t => t.done).length;
  const DT = 'font-mono text-[11px] uppercase tracking-[0.06em] text-gray-600 pt-0.5';
  const DD = 'text-gray-400 min-w-0 break-words';

  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full grid grid-cols-[auto_minmax(0,1fr)] sm:grid-cols-[auto_minmax(0,1fr)_auto] items-center
                   gap-x-3.5 gap-y-2 px-4 sm:px-5 py-3 text-left hover:bg-white/[0.015]
                   focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-500"
      >
        <span title={statusLabel(task.status)}><Dot status={task.status} /></span>
        <span className="min-w-0 flex flex-col gap-1">
          <span className={`text-[14.5px] font-medium leading-snug break-words ${
            task.status === 'done' ? 'text-gray-400' : 'text-gray-50'}`}>{task.title}</span>
          <span className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500">
            <span>{task.source || '—'}</span>
            <span className="font-mono tabular-nums">{day(task.createdAt)}</span>
          </span>
        </span>
        <span className="col-start-2 sm:col-start-auto flex items-center gap-2.5">
          <span className={kind.cls}>{kind.label}</span>
          {task.tests.length > 0 && (
            <span className={`font-mono text-xs tabular-nums whitespace-nowrap ${
              ticked === task.tests.length ? 'text-ok' : 'text-gray-500'}`}>
              {ticked}/{task.tests.length} tests
            </span>
          )}
          <Icon d={CHEVRON} className={`w-4 h-4 text-gray-600 transition-transform duration-200 motion-reduce:transition-none ${
            open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {open && editing && (
        <div className="px-4 sm:px-5 pb-5 pt-1">
          <TaskForm initial={task} submitLabel="Save" busy={busy} onSubmit={onSave} onCancel={onCancelEdit} />
        </div>
      )}

      {open && !editing && (
        <div className="grid gap-x-7 gap-y-4 px-4 sm:px-5 sm:pl-[41px] pb-4 md:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
          <div className="min-w-0">
            {task.details
              ? <p className="max-w-[65ch] text-[13.5px] leading-relaxed text-gray-400 whitespace-pre-wrap break-words">{task.details}</p>
              : <p className="text-[13px] text-gray-600">No details yet.</p>}
            <dl className="mt-3.5 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3.5 gap-y-1.5 text-[12.5px]">
              <dt className={DT}>Status</dt><dd className={DD}>{statusLabel(task.status)}</dd>
              <dt className={DT}>Source</dt><dd className={DD}>{task.source || '—'}</dd>
              <dt className={DT}>Added</dt>
              <dd className={DD}>{day(task.createdAt)}{task.createdBy ? ` · ${task.createdBy}` : ''}</dd>
              <dt className={DT}>Build</dt><dd className={DD}>{task.build || '—'}</dd>
              {task.doneAt && <><dt className={DT}>Done</dt><dd className={DD}>{day(task.doneAt)}</dd></>}
            </dl>
          </div>

          <div className="min-w-0">
            <h4 className="mb-2.5 font-mono text-[11px] uppercase tracking-[0.08em] text-gray-500">What to test</h4>
            {task.tests.length === 0 ? (
              <p className="text-[13px] text-gray-600">Nothing to test listed.</p>
            ) : (
              <ul className="space-y-2">
                {task.tests.map((t, i) => (
                  <li key={`${i}-${t.text}`}>
                    <label className="flex items-start gap-2.5 text-[13px] leading-normal cursor-pointer">
                      <input type="checkbox" className="checkbox mt-0.5" checked={t.done} disabled={busy}
                             onChange={() => onTick(i)} />
                      <span className={t.done ? 'text-gray-500 line-through decoration-gray-600' : 'text-gray-400'}>
                        {t.text}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="md:col-span-2 flex flex-wrap items-center gap-2 pt-3.5 border-t border-line-faint">
            <label htmlFor={`mv${task.id}`} className="text-xs text-gray-500">Move to</label>
            <select id={`mv${task.id}`} className="select h-8 w-auto text-[13px]" value={task.status} disabled={busy}
                    onChange={(e) => onMove(e.target.value)}>
              {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            {task.status !== 'done' && (
              <button type="button" className="btn-ok btn-sm" disabled={busy} onClick={() => onMove('done')}>Mark done</button>
            )}
            <button type="button" className="btn-glass btn-sm" disabled={busy} onClick={onEdit}>Edit</button>
            <span className="flex-1" />
            <button type="button" className={`${delArmed ? 'btn-danger' : 'btn-ghost'} btn-sm`} disabled={busy}
                    onClick={onDelete}>
              {delArmed ? 'Click again to delete' : 'Delete'}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

export default function TasksTab({ product, title, onChanged }) {
  const { toast } = useToast();
  const [tasks, setTasks] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [type, setType] = useState('all');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState({});
  const [editing, setEditing] = useState(null);
  const [showDone, setShowDone] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [busy, setBusy] = useState(null);       // a mentés alatt álló tétel id-je ('new' = az új tétel)
  const [delArm, setDelArm] = useState(null);

  const load = useCallback(async () => {
    setLoadFailed(false);
    try {
      const res = await api.get('/admin/tasks', { params: { product } });
      setTasks(res.data.tasks || []);
    } catch {
      setLoadFailed(true);
      setTasks(prev => prev || []);
    }
  }, [product]);
  useEffect(() => { load(); }, [load]);

  const replace = (task) => setTasks(ts => ts.map(t => (t.id === task.id ? task : t)));

  const create = async (data) => {
    setBusy('new');
    try {
      const res = await api.post('/admin/tasks', { ...data, product });
      setTasks(ts => [res.data.task, ...ts]);
      setOpen(o => ({ ...o, [res.data.task.id]: true }));
      if (res.data.task.status === 'done') setShowDone(true);
      setNewOpen(false);
      toast.success(`Added to ${title}.`);
      onChanged?.();
    } catch (err) {
      toast.error(errorText(err, 'Could not add the item. Try again.'));
    } finally {
      setBusy(null);
    }
  };

  const patch = async (task, data, { optimistic = false, done } = {}) => {
    if (optimistic) replace({ ...task, ...data });
    setBusy(task.id);
    try {
      const res = await api.patch(`/admin/tasks/${task.id}`, data);
      replace(res.data.task);
      if (data.status !== undefined && data.status !== task.status) onChanged?.();
      done?.(res.data.task);
    } catch (err) {
      if (optimistic) replace(task);
      toast.error(errorText(err, 'Could not save the change. Try again.'));
    } finally {
      setBusy(null);
    }
  };

  const tick = (task, i) => patch(task, {
    tests: task.tests.map((t, k) => (k === i ? { ...t, done: !t.done } : t)),
  }, { optimistic: true });

  const move = (task, status) => patch(task, { status }, {
    done: (t) => {
      if (t.status === 'done') setOpen(o => ({ ...o, [t.id]: false }));
      toast.success(`Moved to ${statusLabel(t.status)}.`);
    },
  });

  const save = (task, data) => patch(task, data, { done: () => { setEditing(null); toast.success('Saved.'); } });

  const remove = async (task) => {
    if (delArm !== task.id) { setDelArm(task.id); return; }
    setBusy(task.id);
    try {
      await api.delete(`/admin/tasks/${task.id}`);
      setTasks(ts => ts.filter(t => t.id !== task.id));
      setDelArm(null);
      toast.success('Deleted.');
      onChanged?.();
    } catch (err) {
      toast.error(errorText(err, 'Could not delete the item. Try again.'));
    } finally {
      setBusy(null);
    }
  };

  const term = q.trim().toLowerCase();
  const matches = useCallback((t) => {
    if (type !== 'all' && t.kind !== type) return false;
    if (!term) return true;
    const hay = `${t.title} ${t.details} ${t.source} ${t.build} ${t.tests.map(x => x.text).join(' ')}`.toLowerCase();
    return hay.includes(term);
  }, [type, term]);

  const byStatus = useMemo(() => {
    const out = {};
    for (const sid of GROUP_ORDER) out[sid] = [];
    for (const t of tasks || []) (out[t.status] || out.todo).push(t);
    return out;
  }, [tasks]);

  const jump = (sid) => {
    if (sid === 'done') setShowDone(true);
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    // a csukott „Done” a kinyitás után rajzolódik ki — egy képkockával később görgetünk
    requestAnimationFrame(() => document.getElementById(`tg-${product}-${sid}`)
      ?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' }));
  };

  if (tasks === null) return <div className="py-12 flex justify-center"><LoadingSpinner /></div>;

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-[22px] font-semibold tracking-[-0.025em] text-gray-50">{title}</h2>
          <p className="mt-1 max-w-[62ch] text-[13.5px] text-gray-500">{SUBTITLE[product]}</p>
        </div>
        <button type="button" className="btn-primary btn-sm" onClick={() => setNewOpen(v => !v)} aria-expanded={newOpen}>
          <Icon d={PLUS} className="w-[15px] h-[15px]" />New item
        </button>
      </div>

      {newOpen && (
        <div className="card mt-4 p-5">
          <TaskForm submitLabel="Add item" busy={busy === 'new'} onSubmit={create} onCancel={() => setNewOpen(false)} />
        </div>
      )}

      {loadFailed && (
        <div className="mt-4 flex flex-wrap items-center gap-3 text-[13px] text-[#ff8a80]">
          The list could not be loaded.
          <button type="button" className="btn-glass btn-sm" onClick={load}>Try again</button>
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-2" aria-label="Items by status">
        {GROUP_ORDER.map(sid => (
          <button key={sid} type="button" onClick={() => jump(sid)}
                  className="inline-flex items-center gap-2 h-[30px] px-3 rounded-full border border-line-strong
                             font-mono text-xs text-gray-400 hover:bg-white/[0.04] hover:text-gray-100
                             focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500">
            <Dot status={sid} />
            {statusLabel(sid)} <span className="text-gray-50 tabular-nums">{byStatus[sid].length}</span>
          </button>
        ))}
      </div>

      <div className="mt-3.5 flex flex-wrap items-center gap-2.5">
        <div className="relative flex-1 basis-60 min-w-0">
          <Icon d={SEARCH} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none" />
          <input type="search" className="input pl-9" placeholder="Search tasks" aria-label="Search tasks"
                 value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <FilterTabs filters={TYPE_FILTERS} current={type} onChange={setType} />
      </div>

      {GROUP_ORDER.map(sid => {
        const st = STATUSES.find(s => s.id === sid);
        const all = byStatus[sid];
        const shown = all.filter(matches);
        return (
          <section key={sid} id={`tg-${product}-${sid}`} className="mt-7 scroll-mt-20" aria-label={st.label}>
            <div className="mb-2.5 flex flex-wrap items-center gap-2.5">
              <Dot status={sid} />
              <h3 className="font-mono text-xs font-semibold uppercase tracking-[0.08em] text-gray-100">{st.label}</h3>
              <span className="font-mono text-xs text-gray-500">
                {shown.length === all.length ? all.length : `${shown.length} of ${all.length}`}
              </span>
              {st.hint && <span className="text-xs text-gray-600">{st.hint}</span>}
            </div>
            <div className="card overflow-hidden">
              {shown.length === 0 ? (
                <p className="px-5 py-4 text-[13px] text-gray-600">{all.length ? 'Nothing matches the filter.' : 'Nothing here.'}</p>
              ) : sid === 'done' && !showDone ? (
                <button type="button" onClick={() => setShowDone(true)}
                        className="w-full px-5 py-3 text-left text-[13px] font-medium text-gray-500 hover:text-gray-100 hover:bg-white/[0.015]">
                  Show {shown.length} done item{shown.length > 1 ? 's' : ''}
                </button>
              ) : (
                <ul className="divide-y divide-line-faint">
                  {shown.map(task => (
                    <TaskRow
                      key={task.id}
                      task={task}
                      open={!!open[task.id]}
                      editing={editing === task.id}
                      busy={busy === task.id}
                      delArmed={delArm === task.id}
                      onToggle={() => { setOpen(o => ({ ...o, [task.id]: !o[task.id] })); setDelArm(null); }}
                      onTick={(i) => tick(task, i)}
                      onMove={(status) => move(task, status)}
                      onEdit={() => { setEditing(task.id); setDelArm(null); }}
                      onSave={(data) => save(task, data)}
                      onCancelEdit={() => setEditing(null)}
                      onDelete={() => remove(task)}
                    />
                  ))}
                </ul>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}
